import { useContext, forwardRef, useId, useState, useEffect, useRef, useCallback } from 'react';
import type { ChangeEventHandler, ComponentPropsWithRef, ComponentPropsWithoutRef, FocusEventHandler, MutableRefObject } from 'react';
import { formInlineContext } from '../../visual-helper/FormInline';
import { inputGroupContext } from '../../visual-helper/InputGroup';
import { twMerge } from 'tailwind-merge';
import { gridPlacementClasses } from '@/chassis/core/common-helpers/class-merge';
import FormLabel from '../FormLabel';
import RequiredBadge from '../../visual-helper/RequiredBadge';
import { useLanguage } from '@/chassis/ui/i18n/useLanguage';
import type { Language } from '@/chassis/ui/i18n/i18n-helper';
import { REQUIRED_FIELD_MESSAGE, REQUIRED_VALIDATION_EVENT, type RequiredValidationEventDetail } from '@/chassis/data/services/form-required-validation';

type I18nLabel = Record<Language, string>;

interface FormTextareaProps extends ComponentPropsWithoutRef<'textarea'> {
    formTextareaSize?: 'sm' | 'lg';
    rounded?: boolean;
    label?: string;
    i18nLabel?: I18nLabel | string;
    wrapperClassName?: string;
    requiredMessage?: string;
}

type FormTextareaRef = ComponentPropsWithRef<'textarea'>['ref'];

const FormTextarea = forwardRef((props: FormTextareaProps, ref: FormTextareaRef) => {
    const formInline = useContext(formInlineContext);
    const inputGroup = useContext(inputGroupContext);
    const lang = useLanguage();
    const {
        formTextareaSize,
        rounded,
        label,
        i18nLabel,
        wrapperClassName,
        id,
        name,
        className,
        onBlur,
        onChange,
        requiredMessage = REQUIRED_FIELD_MESSAGE,
        ...rest
    } = props;
    const { ['aria-describedby']: ariaDescribedByProp, ...restProps } = rest as typeof rest & {
        'aria-describedby'?: string;
    };
    const isRequired = Boolean((restProps as { required?: boolean }).required);
    const [showRequiredError, setShowRequiredError] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const generatedId = useId();
    const controlId = id || (name as string | undefined) || generatedId;
    const errorId = `${controlId}-required`;

    const mergeRefs = useCallback(
        (node: HTMLTextAreaElement | null) => {
            textareaRef.current = node;
            if (typeof ref === 'function') {
                ref(node);
            } else if (ref) {
                (ref as MutableRefObject<HTMLTextAreaElement | null>).current = node;
            }
        },
        [ref],
    );
    
    // Use i18nLabel if provided, otherwise fall back to label
    const displayLabel = (typeof i18nLabel === 'string' ? i18nLabel : i18nLabel?.[lang]) || label;

    const isEmptyValue = (value: unknown) => {
        if (value === null || value === undefined) return true;
        if (typeof value === 'string') return value.trim() === '';
        return false;
    };

    const handleBlur: FocusEventHandler<HTMLTextAreaElement> = (event) => {
        if (isRequired) {
            setShowRequiredError(isEmptyValue(event.target.value));
        }
        onBlur?.(event);
    };

    const handleChange: ChangeEventHandler<HTMLTextAreaElement> = (event) => {
        if (isRequired && showRequiredError && !isEmptyValue(event.target.value)) {
            setShowRequiredError(false);
        }
        onChange?.(event);
    };

    useEffect(() => {
        if (!isRequired) return undefined;

        const listener = (event: Event) => {
            const detail = (event as CustomEvent<RequiredValidationEventDetail>).detail;
            const targetForm = detail?.form;
            const element = textareaRef.current;

            if (!element) return;
            if (targetForm && element.form && targetForm !== element.form) return;

            if (isEmptyValue(element.value)) {
                setShowRequiredError(true);
            }
        };

        window.addEventListener(REQUIRED_VALIDATION_EVENT, listener as EventListener);
        return () => window.removeEventListener(REQUIRED_VALIDATION_EVENT, listener as EventListener);
    }, [isRequired]);

    const ariaDescribedBy = [ariaDescribedByProp, showRequiredError ? errorId : null]
        .filter(Boolean)
        .join(' ') || undefined;

    const textareaEl = (
        <textarea
            {...restProps}
            id={controlId}
            name={name}
            ref={mergeRefs}
            onBlur={handleBlur}
            onChange={handleChange}
            aria-invalid={showRequiredError ? 'true' : undefined}
            aria-describedby={ariaDescribedBy}
            className={twMerge([
                'disabled:bg-slate-100 disabled:cursor-not-allowed dark:disabled:bg-darkmode-700/50',
                '[&[readonly]]:bg-slate-100 [&[readonly]]:cursor-not-allowed [&[readonly]]:dark:bg-darkmode-700/50',
                'transition duration-200 ease-in-out w-full text-sm border-slate-300/60 shadow-sm rounded-md placeholder:text-slate-400/90 focus:ring-4 focus:ring-primary focus:ring-opacity-20 focus:border-primary focus:border-opacity-40 dark:bg-darkmode-700 dark:focus:ring-slate-700 dark:focus:ring-opacity-50 dark:placeholder:text-slate-500/80',
                formTextareaSize == 'sm' && 'text-xs py-1.5 px-2',
                formTextareaSize == 'lg' && 'text-lg py-1.5 px-4',
                rounded && 'rounded-full',
                formInline && 'flex-1',
                inputGroup && 'rounded-none [&:not(:first-child)]:border-l-transparent first:rounded-l last:rounded-r z-10',
                showRequiredError && 'border-danger focus:border-danger focus:ring-danger/30',
                className,
            ])}
        />
    );

    const helpText = showRequiredError ? (
        <p id={errorId} className="mt-1 text-xs text-danger">
            {requiredMessage}
        </p>
    ) : null;

    if (!displayLabel)
        return (
            <>
                {textareaEl}
                {helpText}
            </>
        );

    return (
        <div className={twMerge(['w-full', gridPlacementClasses(className), wrapperClassName])}>
            <FormLabel htmlFor={controlId}>
                <span className="inline-flex min-h-[1.25rem] items-center gap-1">
                    <span>{displayLabel}</span>
                    {isRequired && <RequiredBadge />}
                </span>
            </FormLabel>
            {textareaEl}
            {helpText}
        </div>
    );
});

export default FormTextarea;
export { FormTextarea };
